import * as t from 'io-ts';
import {mapValues} from 'lodash';

import {colorForTeammateId} from '../../helpers/teammate/teammateColorHelpers';
import {makeApiSerializableType, makeApiType, RContactUidRef, RTagIdRef, RTeammateIdRef} from './apiTypes';
import {buildContactUid, ContactSourcesEnum} from './contactModel';
import {parseCustomFieldAttribute, RCustomFieldAttributes} from './customFieldAttributeModel';
import {parseTeammateNotificationRules, RTeammateNotificationRules} from './notificationRulesModel';
import {PaletteColorsEnum} from './paletteModel';
import {makeSafeArrayType} from './types/arrayTypes';
import {makeEnumRuntimeType, makeSafeEnumRuntimeType} from './types/enumTypes';
import {makeOptional} from './types/optionalType';

/*
 * Runtime type.
 */

export enum TeammateAccountStatusesEnum {
  ACTIVE = 'active',
  PENDING = 'pending',
  PENDING_EMAIL = 'pending_email',
  BLOCKED = 'blocked',
  DELETED = 'deleted',
}

export enum TeammateLicenseTypesEnum {
  FULL = 'full',
  VISITOR = 'visitor',
  TEMPLATE = 'archetype',
  BOT = 'bot',
  /** Deprecated. For parsing inactive teammates purpose only. */
  LIGHT = 'light',
}

export enum TeammateFlagsEnum {
  IS_ONBOARDING_DEMO = 'is_onboarding_demo',
  IS_UNVERIFIED_DOMAIN_LOGIN = 'is_unverified_domain_login',
  IS_GOOGLE_SIGNUP = 'is_google_signup',
  IS_OFFICE365_SIGNUP = 'is_office_365_signup',
}

export enum TeammateAvailabilityStatusesEnum {
  AVAILABLE = 'available',
  OUT_OF_OFFICE = 'out_of_office',
  BUSY = 'busy',
}

const RTeammateRoleByNamespace = t.record(t.string, makeOptional(t.string));

const RTeammateBase = t.type({
  id: t.Integer,
  url: t.string,
  namespace: t.string,
  email: t.string,
  contactUid: RContactUidRef,
  alias: t.string,
  givenName: t.string,
  familyName: t.string,
  phone: t.string,
  isAvailable: t.boolean,
  availabilityStatus: makeSafeEnumRuntimeType(
    TeammateAvailabilityStatusesEnum,
    TeammateAvailabilityStatusesEnum.AVAILABLE,
  ),
  canEditKnowledgeBase: t.boolean,
  color: makeEnumRuntimeType(PaletteColorsEnum),
  accountStatus: makeEnumRuntimeType(TeammateAccountStatusesEnum),
  licenseType: makeSafeEnumRuntimeType(TeammateLicenseTypesEnum, TeammateLicenseTypesEnum.VISITOR),
  roleByNamespace: RTeammateRoleByNamespace,
  avatarUrl: makeOptional(t.string),
  inboxTagId: RTagIdRef,
  starredTagId: RTagIdRef,
  isOnboardingDemo: t.boolean,
  isUnverified: t.boolean,
  isGoogleSignup: t.boolean,
  isOffice365Signup: t.boolean,
  createdAt: t.Integer,
  updatedAt: t.Integer,
  invitedAt: makeOptional(t.Integer),
  is2FAEnabled: t.boolean,
  customFieldAttributes: makeOptional(RCustomFieldAttributes),
  notificationRules: RTeammateNotificationRules,
  circleIds: makeSafeArrayType(t.Integer),
  timezone: makeOptional(t.string),
});

export function isTeammateModel(teammate: any): teammate is TeammateModel {
  return RTeammate.is(teammate);
}

export const RTeammate = t.readonly(RTeammateBase, 'teammate');
const RTeammatesSearch = t.readonly(
  t.type({
    items: makeSafeArrayType(RTeammate),
    limit: t.Integer,
    total: t.Integer,
  }),
);

const RTeammates = makeSafeArrayType(RTeammate);
const RTeammateIds = makeSafeArrayType(t.number);

/*
 * Type.
 */

export interface TeammateRoleByNamespaceModel extends t.TypeOf<typeof RTeammateRoleByNamespace> {}
export interface TeammateModel extends t.TypeOf<typeof RTeammate> {}
RTeammateIdRef.setTargetType(RTeammate);
export interface TeammatesSearch extends t.TypeOf<typeof RTeammatesSearch> {}

/*
 * IO methods.
 */

export function parseTeammate(src: any): TeammateModel {
  return {
    id: src.id,
    url: src.url,
    namespace: src.namespace,
    email: src.email,
    contactUid: buildContactUid(ContactSourcesEnum.EMAIL, src.email),
    alias: src.alias,
    givenName: src.given_name,
    familyName: src.family_name,
    phone: src.phone || '',
    avatarUrl: (src.has_custom_avatar && src.avatar) || undefined,
    isAvailable: src.available,
    availabilityStatus: src.availability_status || TeammateAvailabilityStatusesEnum.AVAILABLE,
    canEditKnowledgeBase: Boolean(src.can_edit_knowledge_base),
    color: colorForTeammateId(src.id),
    accountStatus: src.account_status,
    licenseType: src.license_type || TeammateLicenseTypesEnum.FULL, // TODO: Remove default value.
    roleByNamespace: src.access && mapValues(src.access, (namespaceAccess) => namespaceAccess.role),
    inboxTagId: src.inbox_tag_id || 0,
    starredTagId: src.starred_tag_id || 0,
    isOnboardingDemo: hasFlag(src, TeammateFlagsEnum.IS_ONBOARDING_DEMO),
    isUnverified: hasFlag(src, TeammateFlagsEnum.IS_UNVERIFIED_DOMAIN_LOGIN),
    isGoogleSignup: hasFlag(src, TeammateFlagsEnum.IS_GOOGLE_SIGNUP),
    isOffice365Signup: hasFlag(src, TeammateFlagsEnum.IS_OFFICE365_SIGNUP),
    createdAt: src.created_at,
    updatedAt: src.updated_at,
    invitedAt: src.invited_at,
    is2FAEnabled: src.two_factor_auth_status === 'enabled',
    customFieldAttributes:
      (Array.isArray(src.custom_field_attributes) &&
        src.custom_field_attributes.map(parseCustomFieldAttribute)) ||
      undefined,
    notificationRules: parseTeammateNotificationRules(src.notification_rules),
    circleIds: src.circle_ids ?? [],
    timezone: src.timezone || undefined,
  };
}

export function serializeTeammate(teammate: Partial<TeammateModel>): any {
  return {
    id: teammate.id,
    namespace: teammate.namespace,
    email: teammate.email,
    alias: teammate.alias,
    given_name: teammate.givenName,
    family_name: teammate.familyName,
    phone: teammate.phone,
    avatar: teammate.avatarUrl,
    availability_status: teammate.availabilityStatus,
    available: teammate.isAvailable,
    account_status: teammate.accountStatus,
    license_type: teammate.licenseType,
    access: teammate.roleByNamespace ? mapValues(teammate.roleByNamespace, (role) => ({role})) : undefined,
  };
}

function parseTeammatesSearch(src: any): TeammatesSearch {
  return {
    items: (src.teammates ?? []).map(parseTeammate),
    limit: src.limit,
    total: src.total,
  };
}

function parseTeammates(src: any): ReadonlyArray<TeammateModel> {
  return (src.team ?? []).map(parseTeammate);
}

function parseTeammatesArchetypes(src: any): ReadonlyArray<TeammateModel> {
  return (src.archetypes ?? []).map(parseTeammate);
}

function parseTeammateIds(src: any): ReadonlyArray<number> {
  return src.teammate_ids ?? [];
}

/*
 * API type.
 */

export const ApiTeammate = makeApiSerializableType('teammate', RTeammate, parseTeammate, serializeTeammate);
export const ApiTeammates = makeApiType('teammates', RTeammates, parseTeammates);
export const ApiTeammateIds = makeApiType('teammate_ids', RTeammateIds, parseTeammateIds);
export const ApiTeammatesSearch = makeApiType('teammatesSearch', RTeammatesSearch, parseTeammatesSearch);
export const ApiTeammatesArchetypes = makeApiType(
  'teammatesArchetypes',
  RTeammates,
  parseTeammatesArchetypes,
);

/*
 * Helpers.
 */

function hasFlag(src: any, flag: TeammateFlagsEnum) {
  return Array.isArray(src.extra) && src.extra.includes(flag);
}
